/**
 * Output Schemas Tab
 * Manages enabled schemas for this Agent (references centralized output_schemas table)
 */

import React, { useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FileJson, Star, Check, ExternalLink, Info, Loader2 } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useOutputSchemas } from '@/features/system-settings/hooks/useOutputSchemas';
import type { OutputSchema } from '@/types/outputSchema';

interface OutputSchemasTabProps {
  enabledSchemaKeys: string[];
  defaultSchemaKey: string | null;
  onEnabledKeysChange: (keys: string[]) => void;
  onDefaultKeyChange: (key: string | null) => void;
}

export const OutputSchemasTab: React.FC<OutputSchemasTabProps> = ({
  enabledSchemaKeys,
  defaultSchemaKey,
  onEnabledKeysChange,
  onDefaultKeyChange,
}) => {
  const { messages, language } = useI18n();
  const t = messages.agentConfig?.outputSchemas;

  // Fetch all available schemas from centralized table
  const { data, isLoading } = useOutputSchemas({ enabled: true });
  const allSchemas: OutputSchema[] = data?.schemas || [];

  // Separate enabled and available schemas
  const { enabledSchemas, availableSchemas } = useMemo(() => {
    const enabled: OutputSchema[] = [];
    const available: OutputSchema[] = [];
    allSchemas.forEach((schema: OutputSchema) => {
      if (enabledSchemaKeys.includes(schema.key)) {
        enabled.push(schema);
      } else {
        available.push(schema);
      }
    });
    // Sort enabled schemas by their order in enabledSchemaKeys
    enabled.sort((a, b) =>
      enabledSchemaKeys.indexOf(a.key) - enabledSchemaKeys.indexOf(b.key)
    );
    return { enabledSchemas: enabled, availableSchemas: available };
  }, [allSchemas, enabledSchemaKeys]);

  const handleToggleSchema = (schemaKey: string) => {
    if (enabledSchemaKeys.includes(schemaKey)) {
      // Remove from enabled
      const newKeys = enabledSchemaKeys.filter((k) => k !== schemaKey);
      onEnabledKeysChange(newKeys);
      // Clear default if it was the removed schema
      if (defaultSchemaKey === schemaKey) {
        onDefaultKeyChange(newKeys.length > 0 ? newKeys[0] : null);
      }
    } else {
      // Add to enabled
      onEnabledKeysChange([...enabledSchemaKeys, schemaKey]);
    }
  };

  const handleSetDefault = (schemaKey: string) => {
    onDefaultKeyChange(schemaKey);
  };

  const handleGoToSchemaSettings = () => {
    window.open('/system-settings?tab=output-schemas', '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              {language === 'zh'
                ? '从集中管理的 Schema 库中选择要为此 Agent 启用的输出格式。'
                : 'Select output schemas to enable for this Agent from the centralized schema library.'}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoToSchemaSettings}
              className="mt-2 text-blue-700 hover:text-blue-800"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              {language === 'zh' ? '管理全局 Schema 库' : 'Manage Global Schema Library'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Enabled Schemas */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Check className="w-5 h-5 text-green-600" />
            <h3 className="font-medium text-gray-900">
              {language === 'zh' ? '已启用的 Schema' : 'Enabled Schemas'}
            </h3>
            <span className="text-sm text-gray-500">({enabledSchemas.length})</span>
          </div>

          {enabledSchemas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileJson className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {language === 'zh' ? '暂无启用的 Schema' : 'No schemas enabled'}
              </p>
              <p className="text-xs mt-1">
                {language === 'zh'
                  ? '从右侧选择要启用的 Schema'
                  : 'Select schemas from the right to enable'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {enabledSchemas.map((schema) => (
                <div
                  key={schema.key}
                  className={`p-3 rounded-lg border transition-colors ${
                    defaultSchemaKey === schema.key
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {schema.name}
                        </span>
                        {defaultSchemaKey === schema.key && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-200 text-amber-800 rounded">
                            <Star className="w-3 h-3 fill-current" />
                            {t?.default || (language === 'zh' ? '默认' : 'Default')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 font-mono mt-1">
                        {schema.key}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {defaultSchemaKey !== schema.key && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(schema.key)}
                          title={language === 'zh' ? '设为默认' : 'Set as Default'}
                        >
                          <Star className="w-4 h-4 text-gray-400 hover:text-amber-500" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleSchema(schema.key)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {language === 'zh' ? '移除' : 'Remove'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Available Schemas */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <FileJson className="w-5 h-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">
              {language === 'zh' ? '可用的 Schema' : 'Available Schemas'}
            </h3>
            <span className="text-sm text-gray-500">({availableSchemas.length})</span>
          </div>

          {availableSchemas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Check className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {allSchemas.length === 0
                  ? (language === 'zh' ? '暂无可用的 Schema' : 'No schemas available')
                  : (language === 'zh' ? '所有 Schema 已启用' : 'All schemas are enabled')}
              </p>
              {allSchemas.length === 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleGoToSchemaSettings}
                  className="mt-3"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {language === 'zh' ? '创建新 Schema' : 'Create New Schema'}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {availableSchemas.map((schema) => (
                <div
                  key={schema.key}
                  className="p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {schema.name}
                        </span>
                        {schema.scope === 'agent' && schema.agent_type && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                            {schema.agent_type}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 font-mono mt-1">
                        {schema.key}
                      </div>
                      {schema.description && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {schema.description}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleToggleSchema(schema.key)}
                      className="ml-2"
                    >
                      {language === 'zh' ? '启用' : 'Enable'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default OutputSchemasTab;
